import { memo, useRef, useMemo, useReducer, useCallback, Key, useState, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Form, Input, Modal, Select, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import { Crud, CrudFormComponent, CrudMode, CrudProps, notification, rejectionError, IconButton } from '@ohpen/enso-ui';
import type { FormItem } from '@ohpen/enso-ui';
import { CreateExportTemplateRequest, ExportTemplateResponse, createExportTemplate, updateExportTemplate } from '@pmp/api';
import { useFeatureToggle } from '@pmp/hooks';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, actionEntityTypeToTranslationKey, getApiErrorMessage, textLimitProps } from '@pmp/utils';
import { useAdditionalFieldsStore, useTableViewsStore } from '@pmp/stores';

import { ExportCrudProps } from '../interfaces';
import styles from '../styles.module.css';
import { getFormSchema } from '../formConfig';
import { getColumnsSchema } from '../utils';
import { initialState, reducer } from '../reducer';

export const ExportTemplatesCrudForm = memo(({ entityType, mode, record, onClose, refreshTable }: ExportCrudProps): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const enableAdditionalFields = useFeatureToggle(['enableAdditionalFields']);
  const { getAdditionalFields } = useAdditionalFieldsStore();
  const { getTableViews } = useTableViewsStore();
  const [forceUpdate, setForceUpdate] = useState(false);
  const { t } = useTranslation();
  const isReadOnly = mode === CrudMode.VIEW;

  useEffect(() => {
    if (!mode || !entityType) {
      notification().error({ title: t('global.errors.unexpected') });
      onClose();
      return;
    }

    const entityColumns = getColumnsSchema(entityType).map(column => column.key);
    const defaultOrder = entityColumns.filter(key => key !== 'actions');
    const missingColumns = defaultOrder.filter(column => !record?.columnOrder?.includes(column as string));
    const dataColumnOrder = record?.columnOrder ?? [];

    getAdditionalFields(entityType)
      .then(additionalFields => {
        const additionalFieldsArray = Object.keys(additionalFields ?? {});
        const missingAdditionalFields = additionalFieldsArray.filter(field => !dataColumnOrder.includes(field));
        const columnOrder = [...dataColumnOrder, ...missingColumns, ...(enableAdditionalFields ? missingAdditionalFields : [])];
        const additionalColumns = additionalFieldsArray.reduce((acc, key) => ({ ...acc, [key]: key }), {});
        const columnMapping = enableAdditionalFields ? { ...record?.columnMapping, ...additionalColumns } : (record?.columnMapping ?? {});

        dispatch({
          type: 'open',
          data: { ...record, columnOrder, columnMapping, entityType: { key: entityType } } as ExportTemplateResponse,
          additionalFields,
          additionalFieldsArray,
        });
      })
      .catch(error => {
        notification().error({ title: getApiErrorMessage(error) });
        onClose();
      });
  }, [mode, enableAdditionalFields, entityType, getAdditionalFields, onClose, record, t]);

  const onConfirm = useCallback(
    async (values: CreateExportTemplateRequest): Promise<void> => {
      if (!state.data || !entityType) throw new Error('Missing required data');
      if (mode === CrudMode.VIEW) return;
      if (mode === CrudMode.CREATE || mode === CrudMode.CLONE) {
        if (!values.name || !values.description || !state.data.columnOrder?.length)
          throw rejectionError(t('global.errors.invalidData'), values);

        const data: CreateExportTemplateRequest = {
          name: values.name,
          description: values.description,
          columnOrder: state.data.columnOrder,
          columnMapping: values.columnMapping ?? {},
          columnVisibility: values.columnVisibility ?? {},
          entityType: values.entityType,
        };

        return createExportTemplate({ body: data }).then(() => {
          notification().success({ title: t('enso.notifications.xCreatedSuccessfully', { x: t('global.labels.exportTemplate') }) });
          refreshTable();
        });
      }
      if (mode === CrudMode.EDIT) {
        if (!state.data?.id || state.data?.versionNumber === undefined || !values.name)
          throw rejectionError(t('global.errors.invalidData'), { state, values });
        const data: CreateExportTemplateRequest = {
          name: values.name,
          description: values.description,
          columnOrder: state.data.columnOrder,
          columnMapping: values.columnMapping ?? {},
          columnVisibility: values.columnVisibility ?? {},
          entityType: values.entityType,
        };
        return updateExportTemplate({
          path: { templateId: state.data.id },
          query: { version: state.data.versionNumber },
          body: data,
        }).then(() => {
          notification().success({ title: t('enso.notifications.xEditedSuccessfully', { x: t('global.labels.exportTemplate') }) });
          refreshTable();
        });
      }
      throw rejectionError(t('enso.errors.crud.noMode'), state);
    },
    [mode, entityType, refreshTable, state, t],
  );

  const onCopyView = useCallback(async () => {
    if (!entityType) {
      notification().error({ title: t('global.errors.unexpected') });
      return;
    }
    dispatch({ type: 'openViewsModal' });

    try {
      const tableViews = await getTableViews(entityType);
      dispatch({ type: 'setTableViews', tableViews });
    } catch (error) {
      notification().error({ title: getApiErrorMessage(error) });
      dispatch({ type: 'closeViewsModal' });
    }
  }, [getTableViews, entityType, t]);

  const onChangeSelectView = useCallback((selectedView: string) => {
    dispatch({ type: 'selectView', selectedView });
  }, []);

  const onConfirmViewModal = useCallback(() => {
    const view = state.viewsModal.tableViews?.find(view => view.id === state.viewsModal.selectedView);

    if (!view || !ref.current?.form) {
      notification().error({ title: t('global.errors.unexpected') });
      return;
    }

    const { columnOrder } = ref.current.form.getFieldsValue(['columnOrder']);
    const newColumnOrder = [
      ...view.order.filter((column: string) => column !== 'actions'),
      ...columnOrder.filter((column: string) => !view.order.includes(column) && !column.includes('customColumn-')),
      ...columnOrder.filter((column: string) => column.includes('customColumn-')),
    ];
    const newVisibility = view.order.reduce((acc, key) => ({ ...acc, [key]: view.selected.includes(key) }), {});

    ref?.current.form.setFieldsValue({
      columnOrder: newColumnOrder,
      columnVisibility: newVisibility,
    });
    ref.current.setDisableSave(false);

    setForceUpdate((prev: boolean) => !prev);
    dispatch({ type: 'closeViewsModal' });
  }, [state.viewsModal.selectedView, state.viewsModal.tableViews, t]);

  const onCancelModal = useCallback(() => {
    dispatch({ type: 'closeViewsModal' });
  }, []);

  const onDragEnd = useCallback((columnOrder: Key[]) => {
    dispatch({ type: 'setColumnOrder', columnOrder: columnOrder as string[] });
    ref.current?.form.setFieldsValue({ columnOrder });
    setForceUpdate((prev: boolean) => !prev);
    ref.current?.setDisableSave(false);
  }, []);

  const onAddColumn = useCallback(() => {
    if (!state.data || !ref.current) return;
    const newKey = `customColumn-${Date.now()}`;
    const { columnMapping, columnVisibility } = ref.current.form.getFieldsValue(['columnMapping', 'columnVisibility']);

    dispatch({ type: 'setColumnOrder', columnOrder: [...state.data.columnOrder, newKey] });

    ref.current?.form.setFieldsValue({
      columnOrder: [...state.data.columnOrder, newKey],
      columnMapping: { ...columnMapping, [newKey]: t('global.labels.customColumn') },
      columnVisibility: { ...columnVisibility, [newKey]: true },
    });
    ref.current?.setDisableSave(false);
    setForceUpdate((prev: boolean) => !prev);
  }, [state.data, t]);

  const onDeleteColumn = useCallback(
    (key: string) => {
      if (!ref.current || !state.data) return;
      const newColumnOrder = state.data.columnOrder.filter((column: string) => column !== key);

      // setFieldsValue uses deep-merge and cannot remove keys, so we mutate the raw store directly
      const store = ref.current.form.getFieldsValue(true) as {
        columnMapping?: Record<string, unknown>;
        columnVisibility?: Record<string, unknown>;
      };
      delete store.columnMapping?.[key];
      delete store.columnVisibility?.[key];

      dispatch({ type: 'setColumnOrder', columnOrder: newColumnOrder });
      ref.current.form.setFieldsValue({ columnOrder: newColumnOrder });
      ref.current.setDisableSave(false);
      setForceUpdate((prev: boolean) => !prev);
    },
    [state.data],
  );

  const handleOnClose = useCallback(() => {
    dispatch({ type: 'close' });
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const columnLabels = useMemo(
    () =>
      (entityType ? getColumnsSchema(entityType) : []).reduce<Record<string, string>>((acc, column) => {
        if (column.key && typeof column.title === 'string') acc[column.key.toString()] = column.title;
        return acc;
      }, {}),
    [entityType],
  );

  const getRow = useCallback(
    (key: Key) => {
      const isCustom = key.toString().includes('customColumn-');

      function getTitle(): string {
        if (isCustom) return t('global.labels.customColumn');
        if (state.additionalFieldsArray?.includes(key as string)) {
          return state.additionalFields?.[key as string] ?? key.toString();
        }
        // A key the list does not show, such as a column of the Export to Import template, keeps its entity label.
        const label = columnLabels[key.toString()];
        if (label) return t(label);
        return entityType ? t(`pmp.${actionEntityTypeToTranslationKey[entityType]}.labels.${key}`) : key.toString();
      }

      return (
        <div>
          <div className={styles.rowLabel}>{getTitle()}</div>
          <div className={styles.rowControls}>
            <div className={styles.rowInput}>
              <Form.Item name={['columnMapping', key] as [string, string | number]} className={styles.rowFormItem}>
                <Input disabled={isReadOnly} {...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT)} placeholder={isReadOnly ? '' : undefined} />
              </Form.Item>
            </div>
            <Tooltip placement="topRight" title={t('global.labels.exportThisColumn')}>
              <Form.Item
                name={['columnVisibility', key] as [string, string | number]}
                valuePropName="checked"
                className={styles.rowFormItem}
              >
                <Checkbox disabled={isReadOnly} />
              </Form.Item>
            </Tooltip>
            <IconButton icon={<DeleteOutlined />} onClick={() => onDeleteColumn(key as string)} disabled={!isCustom || isReadOnly} />
          </div>
        </div>
      );
    },
    [isReadOnly, t, state.additionalFieldsArray, columnLabels, entityType, state.additionalFields, onDeleteColumn],
  );

  const viewOptions = useMemo(
    () => state.viewsModal.tableViews?.map(({ id, name }) => ({ value: id, label: name })),
    [state.viewsModal.tableViews],
  );

  const formSchema = useMemo((): FormItem[] | null => {
    if (!state.data || !state.open || forceUpdate === undefined) return null;

    const required = [{ required: true, message: t('global.errors.required') }];

    return getFormSchema({ mode, isReadOnly, entityType, required, getRow, onDragEnd, onCopyView, onAddColumn });
  }, [state.data, state.open, forceUpdate, t, entityType, mode, isReadOnly, onCopyView, onDragEnd, onAddColumn, getRow]);

  const crudProps: CrudProps<CreateExportTemplateRequest> = useMemo(() => {
    const titles: { [key: string]: string } = {
      [CrudMode.CREATE]: t('enso.labels.createX', { x: t('global.labels.exportTemplate') }),
      [CrudMode.CLONE]: t('global.labels.cloneX', { x: t('global.labels.exportTemplate') }),
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('global.labels.exportTemplate') }),
      [CrudMode.EDIT]: t('enso.labels.editX', { x: t('global.labels.exportTemplate') }),
    };
    const confirms: { [key: string]: string } = {
      [CrudMode.CREATE]: t('enso.labels.create'),
      [CrudMode.CLONE]: t('global.labels.clone'),
      [CrudMode.EDIT]: t('enso.labels.save'),
    };

    return {
      ref,
      open: state.open,
      title: (mode && titles[mode]) ?? '',
      labels: {
        confirm: (mode && confirms[mode]) ?? '',
      },
      initialValues: { ...state.data, entityType: entityType } as CreateExportTemplateRequest,
      mode,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [t, state.open, state.data, mode, entityType, handleOnClose, onConfirm]);

  return (
    <>
      <Crud {...crudProps} schema={formSchema ?? undefined} />
      <Modal
        title={t('global.labels.selectView')}
        open={state.viewsModal?.open}
        onOk={onConfirmViewModal}
        okButtonProps={{ disabled: !state.viewsModal.selectedView }}
        okText={t('global.buttons.copy')}
        onCancel={onCancelModal}
        zIndex={1001}
        centered={true}
      >
        <Select
          className={styles.select}
          value={state.viewsModal.selectedView}
          options={viewOptions}
          loading={state.viewsModal.loading}
          size="large"
          onChange={onChangeSelectView}
          placeholder={t('global.labels.selectView') + '...'}
        />
      </Modal>
    </>
  );
});
